import React, { useRef } from 'react';
import './styles.css';

interface FloatingToolbarProps {
  onAddNode: (nodeType: string) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetView: () => void;
  onNodeDragStart?: (
    e: React.DragEvent<HTMLButtonElement>,
    nodeType: {
      type: string;
      name: string;
      icon: React.ReactNode;
      color: string;
    }
  ) => void;
  onNodeDragEnd?: () => void;
}

const FloatingToolbar: React.FC<FloatingToolbarProps> = ({
  onAddNode,
  onZoomIn,
  onZoomOut,
  onResetView,
  onNodeDragStart,
  onNodeDragEnd,
}) => {
  // 用于追踪是否正在进行拖拽操作，避免拖拽结束时触发点击
  const isDraggingRef = useRef(false);
  // 节点类型定义 - 只保留对象和过程
  const nodeTypes = [
    {
      type: 'rect',
      name: '对象',
      color: '#70E483',
      icon: (
        <svg
          width="56"
          height="36"
          viewBox="0 0 77 51"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="20"
            y="12"
            width="37"
            height="25"
            rx="3"
            fill="none"
            stroke="#70E483"
            strokeWidth="3"
          />
        </svg>
      ),
    },
    {
      type: 'polygon', // 修正：过程应该是polygon类型，不是circle
      name: '过程',
      color: '#3BC3FF',
      icon: (
        <svg
          width="56"
          height="36"
          viewBox="0 0 77 51"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <ellipse
            cx="38.5"
            cy="24"
            rx="18"
            ry="12"
            fill="none"
            stroke="#3BC3FF"
            strokeWidth="3"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className="floating-toolbar">
      {/* 节点类型按钮 */}
      {nodeTypes.map(nodeType => (
        <button
          key={nodeType.type}
          className="floating-toolbar-button node-type-button"
          onClick={() => {
            // 如果刚刚进行了拖拽操作，不触发点击
            if (isDraggingRef.current) {
              isDraggingRef.current = false;
              return;
            }
            onAddNode(nodeType.type);
          }}
          onDragStart={
            onNodeDragStart
              ? e => {
                  isDraggingRef.current = true;
                  onNodeDragStart(e, nodeType);
                }
              : undefined
          }
          onDragEnd={() => {
            // 延迟重置拖拽状态，确保 click 事件能检测到
            setTimeout(() => {
              isDraggingRef.current = false;
            }, 100);
            onNodeDragEnd?.();
          }}
          draggable="true"
          title={nodeType.name}
        >
          {nodeType.icon}
        </button>
      ))}

      {/* 分隔线 */}
      <div className="floating-toolbar-divider"></div>

      {/* 视图控制按钮 */}

      <button
        className="floating-toolbar-button"
        onClick={onZoomIn}
        title="放大画布"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.9167 0.625C5.23458 0.625 0.625 5.23458 0.625 10.9167C0.625 16.5988 5.23458 21.2083 10.9167 21.2083C13.3704 21.2083 15.6183 20.3525 17.3896 18.9225L21.3708 22.9038C21.7933 23.3263 22.4813 23.3263 22.9038 22.9038C23.3263 22.4813 23.3263 21.7933 22.9038 21.3708L18.9225 17.3896C20.3525 15.6238 21.2083 13.3704 21.2083 10.9167C21.2083 5.23458 16.5988 0.625 10.9167 0.625ZM2.79167 10.9167C2.79167 6.43167 6.43167 2.79167 10.9167 2.79167C15.4017 2.79167 19.0417 6.43167 19.0417 10.9167C19.0417 15.4017 15.4017 19.0417 10.9167 19.0417C6.43167 19.0417 2.79167 15.4017 2.79167 10.9167ZM12 7.66667C12 7.07083 11.5125 6.58333 10.9167 6.58333C10.3208 6.58333 9.83333 7.07083 9.83333 7.66667V9.83875H7.67208C7.07625 9.83875 6.58875 10.3263 6.58875 10.9275C6.58875 11.5233 7.07625 12.0108 7.67208 12.0108H9.82792V14.1667C9.82792 14.7625 10.3154 15.25 10.9113 15.25C11.5071 15.25 11.9946 14.7625 11.9946 14.1667V12.0054H14.1613C14.7571 12.0054 15.2446 11.5179 15.2446 10.9167C15.2446 10.3208 14.7571 9.83333 14.1613 9.83333H11.9946V7.66667H12Z"
            fill="#121619"
          />
        </svg>
      </button>

      <button
        className="floating-toolbar-button"
        onClick={onZoomOut}
        title="缩小画布"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 26 26"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.9167 1.625C6.23458 1.625 1.625 6.23458 1.625 11.9167C1.625 17.5988 6.23458 22.2083 11.9167 22.2083C14.3704 22.2083 16.6183 21.3525 18.3896 19.9225L22.3708 23.9038C22.7933 24.3263 23.4813 24.3263 23.9038 23.9038C24.3263 23.4813 24.3263 22.7933 23.9038 22.3708L19.9225 18.3896C21.3525 16.6238 22.2083 14.3704 22.2083 11.9167C22.2083 6.23458 17.5988 1.625 11.9167 1.625ZM3.79167 11.9167C3.79167 7.43167 7.43167 3.79167 11.9167 3.79167C16.4017 3.79167 20.0417 7.43167 20.0417 11.9167C20.0417 16.4017 16.4017 20.0417 11.9167 20.0417C7.43167 20.0417 3.79167 16.4017 3.79167 11.9167ZM8.66667 10.8333C8.07083 10.8333 7.58333 11.3208 7.58333 11.9167C7.58333 12.5125 8.07083 13 8.66667 13H15.1667C15.7625 13 16.25 12.5125 16.25 11.9167C16.25 11.3208 15.7625 10.8333 15.1667 10.8333H8.66667Z"
            fill="#121619"
          />
        </svg>
      </button>
      
      <button
        className="floating-toolbar-button"
        onClick={onResetView}
        title="重置视图"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 26 26"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.9167 3.30962C7.39918 3.80796 3.80793 7.39921 3.30959 11.9167H5.95834C6.55418 11.9167 7.04168 12.4042 7.04168 13C7.04168 13.5959 6.55418 14.0834 5.95834 14.0834H3.30959C3.80793 18.6009 7.39918 22.1921 11.9167 22.6905V20.0417C11.9167 19.4459 12.4042 18.9584 13 18.9584C13.5958 18.9584 14.0833 19.4459 14.0833 20.0417V22.6905C18.6008 22.1921 22.1921 18.6009 22.6904 14.0834H19.5C18.9042 14.0834 18.4167 13.5959 18.4167 13C18.4167 12.4042 18.9042 11.9167 19.5 11.9167H22.6904C22.1921 7.39921 18.6008 3.80796 14.0833 3.30962V5.95837C14.0833 6.55421 13.5958 7.04171 13 7.04171C12.4042 7.04171 11.9167 6.55421 11.9167 5.95837V3.30962ZM13 1.08337C19.5813 1.08337 24.9167 6.41879 24.9167 13C24.9167 19.5813 19.5813 24.9167 13 24.9167C6.41876 24.9167 1.08334 19.5813 1.08334 13C1.08334 6.41879 6.41876 1.08337 13 1.08337Z"
            fill="#121619"
          />
        </svg>
      </button>
    </div>
  );
};

export default FloatingToolbar;
